import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTransitionNumberRequest } from "@/features/number-requests/hooks/use-number-requests";
import type { NumberRequest } from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { formatPhoneNumber } from "react-phone-number-input";
import { toast } from "sonner";
import { NumberRequestStateBadge } from "../../components/number-requests/state-badge";
import { DocumentChecklist } from "./document-checklist";

/**
 * A request opened through the guided creation, as its page shows it: the
 * dialog asked everything, so there are no blocks to fill in — only the
 * number, who it is for, and where it stands.
 *
 * The one thing that can still be the client's is a document: a foreign
 * company's registration paper if its upload did not go through, or one our
 * review sent the request back for. Then, and only then, the checklist shows,
 * with the button that sends the request back to us.
 */
export function GuidedStatus({
  clientId,
  request,
}: {
  clientId: string;
  request: NumberRequest;
}) {
  const { t } = useTranslation("numberRequests");
  const transition = useTransitionNumberRequest(clientId);

  const documents = request.documents?.items || [];
  const waitingOnClient = ["draft", "documents_pending"].includes(
    request.state,
  );
  const complete = documents.every((document) => document.provided);

  const send = async () => {
    try {
      await transition.mutateAsync({
        requestId: request.id,
        state: "documents_review",
      });
      toast.success(t("numberRequests.messages.stateUpdated"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("numberRequests.messages.stateFailed"),
      );
    }
  };

  const number = request.phone_number
    ? formatPhoneNumber(request.phone_number) || request.phone_number
    : "";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("numberRequests.status.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {request.kind !== "sip_trunk" && (
            <div className="space-y-1">
              <p className="text-muted-foreground text-xs font-medium uppercase">
                {t("numberRequests.guided.number")}
              </p>
              {number ? (
                <p className="text-2xl font-semibold tabular-nums">{number}</p>
              ) : (
                <p className="text-sm">
                  {request.desired_prefix
                    ? t("numberRequests.guided.numberToCome", {
                        prefix: request.desired_prefix,
                      })
                    : t("numberRequests.notAssignedYet")}
                </p>
              )}
            </div>
          )}

          <div className="space-y-1">
            <p className="text-muted-foreground text-xs font-medium uppercase">
              {t("numberRequests.guided.holder")}
            </p>
            {request.holder_type === "individual" ? (
              <p className="text-sm">
                {t("numberRequests.create.holder.individual")}
              </p>
            ) : (
              <div className="space-y-0.5 text-sm">
                <p className="flex items-center gap-1.5 font-medium">
                  {request.company_name || request.company_siren}
                  {request.company_check === "verified" && (
                    <CheckCircleIcon
                      className="text-primary h-4 w-4"
                      weight="fill"
                      aria-label={t("numberRequests.guided.verified")}
                    />
                  )}
                </p>
                {request.company_siren && (
                  <p className="text-muted-foreground tabular-nums">
                    SIREN {request.company_siren}
                  </p>
                )}
                {request.company_address && (
                  <p className="text-muted-foreground">
                    {request.company_address}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <NumberRequestStateBadge state={request.state} />
            <p className="text-muted-foreground text-sm">
              {/* Review reads "your papers" everywhere else; here there were none. */}
              {t(
                request.state === "documents_review"
                  ? "numberRequests.guided.explain.documents_review"
                  : `numberRequests.status.explain.${request.state}`,
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {waitingOnClient && documents.length > 0 && (
        <DocumentChecklist
          clientId={clientId}
          requestId={request.id}
          kind={request.kind}
          documents={documents}
          country={request.country}
          intro={t("numberRequests.guided.documentsIntro")}
        />
      )}

      {waitingOnClient && (
        <div className="flex justify-end">
          <Button onClick={send} disabled={!complete || transition.isPending}>
            {t("numberRequests.create.send")}
          </Button>
        </div>
      )}
    </div>
  );
}
